import React, { useMemo } from 'react'
import { ColorMarker, Flex, Link, Charts } from '@ui'
import { COLORS } from '@ui/charts'
import { Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { prettyInteger } from '../../utils/numberFormat'
import {
  ITooltipByUsageType,
  ITooltipByUnoccupiedInDepartments,
  ITooltipByAllocatedInDepartments,
  ITooltipByDepartmentsArea,
  ITooltipByEmployeesInDepartment,
} from './hooks/usePortfolioTooltips'
import { SimpleBar } from './Tabs/SeatedEmployeesByDepartment'
import numeral from 'numeral'
import { DepartmentTooltipContent } from './Tabs/SeatsByDepartment'
import css from './portfolioTooltips.module.scss'

interface IDepartment {
  id: string
  name: string
  color: string
}

export type ITooltipTriggerField =
  | 'workstations'
  | 'unoccupied'
  | 'occupied'
  | 'seatedCount'
  | 'areaSqft'
  | 'areaSqrm'
  | 'densitySqft'
  | 'densitySqrm'

export function totalFormatter(total: number) {
  const type = '0,0[.]0a'

  return numeral(total).format(type)
}

const placeholder = <span className={Classes.SKELETON}>Placeholder text Placeholder text</span>

function TableSkeleton() {
  return (
    <Flex direction="column" style={{ marginTop: 30, width: 600 }}>
      <div className={Classes.SKELETON} style={{ margin: '5px 10px 5px 50px', height: 30 }}>
        placeholder
      </div>
      <div className={Classes.SKELETON} style={{ margin: '5px 10px 5px 50px', height: 30 }}>
        placeholder
      </div>
      <div className={Classes.SKELETON} style={{ margin: '5px 10px 5px 50px', height: 30 }}>
        placeholder
      </div>
    </Flex>
  )
}

function DepartmentItem({ name, color }: IDepartment) {
  return (
    <Flex align="center">
      <div className={css.square} style={{ backgroundColor: color }} />
      <div className={css.department_name}>{name}</div>
    </Flex>
  )
}

export function TooltipTrigger({
  field,
  totalSeats,
  availableSeats,
  departmentSeats,
  totalEmployees,
  departmentsArea,
}: {
  field: ITooltipTriggerField
  totalSeats: JSX.Element
  availableSeats: JSX.Element
  departmentSeats: JSX.Element
  totalEmployees: JSX.Element
  departmentsArea: JSX.Element
}): JSX.Element | null {
  switch (field) {
    case 'workstations':
      return totalSeats
    case 'unoccupied':
      return availableSeats
    case 'occupied':
      return departmentSeats
    case 'seatedCount':
      return totalEmployees
    case 'areaSqft':
    case 'areaSqrm':
      return departmentsArea

    default:
      return null
  }
}

function ShowDetails({
  data,
  link,
}: {
  data:
    | ITooltipByEmployeesInDepartment[]
    | ITooltipByDepartmentsArea[]
    | ITooltipByUnoccupiedInDepartments[]
    | ITooltipByUsageType[]
    | ITooltipByAllocatedInDepartments[]
    | undefined
  link: () => void
}) {
  if (!data) return <div>{placeholder}</div>

  return (
    <Flex style={{ marginTop: 4, fontSize: '12px' }}>
      {data.length > 3 && (
        <span style={{ whiteSpace: 'nowrap', marginRight: 10 }}>Top 3/{data.length} shown</span>
      )}
      <Link onClick={link} style={{ whiteSpace: 'nowrap' }}>
        Show Details
      </Link>
    </Flex>
  )
}

export function TotalEmployees({
  data,
  link,
}: {
  data: ITooltipByEmployeesInDepartment[] | undefined
  link: () => void
}) {
  const scale = useMemo(() => Charts.colorScale(), [])

  return (
    <Flex style={{ padding: '15px 15px' }} direction="column">
      <span style={{ fontWeight: 600, fontSize: '16px', textAlign: 'left' }}>
        Employees by Department
      </span>
      <ShowDetails data={data} link={link} />
      {data && (
        <Flex style={{ margin: 20, lineHeight: '30px' }} direction="column">
          {data.slice(0, 3).map((d, index, arr) => (
            <Flex key={d.name} style={{ marginTop: 5 }}>
              <span style={{ width: '30%' }}>{d.name}</span>
              <div
                style={{
                  textAlign: 'right',
                  margin: '0 30px 0 10px',
                  width: '15%',
                }}
              >
                {d.total}
              </div>
              <SimpleBar val={d.total} max={arr[0].total} barColor={`#${scale(d.name)}`} />
            </Flex>
          ))}
        </Flex>
      )}
      {!data && <TableSkeleton />}
    </Flex>
  )
}

export function DepartmentsArea({
  units,
  data,
  link,
}: {
  units: string | undefined
  data: ITooltipByDepartmentsArea[] | undefined
  link: () => void
}) {
  const unitsForShow = units === 'sqm' ? 'Sqm' : 'Sqft'
  const scale = useMemo(() => Charts.colorScale(), [])

  return (
    <Flex style={{ padding: '15px 15px' }} direction="column">
      <span style={{ fontWeight: 600, fontSize: '16px', textAlign: 'left' }}>
        Allocated Area ({unitsForShow ? unitsForShow : 'Sqft'}) By Department
      </span>
      <ShowDetails data={data} link={link} />
      {data && (
        <Flex style={{ margin: 20, lineHeight: '30px' }} direction="column">
          {data.slice(0, 3).map((d, index, arr) => {
            const area = unitsForShow === 'Sqm' ? d.areaSqrm : d.areaSqft

            return (
              <Flex key={d.name} style={{ marginTop: 5 }}>
                <span style={{ width: '30%' }}>{d.name}</span>
                <div
                  style={{
                    textAlign: 'right',
                    margin: '0 30px 0 10px',
                    width: '15%',
                  }}
                >
                  {prettyInteger(area)}
                </div>
                <SimpleBar
                  val={Number(area)}
                  max={Number(unitsForShow === 'Sqm' ? arr[0].areaSqrm : arr[0].areaSqft)}
                  barColor={`#${scale(d.name)}`}
                />
              </Flex>
            )
          })}
        </Flex>
      )}
      {!data && <TableSkeleton />}
    </Flex>
  )
}

export function AvailableSeats({
  allocated,
  unallocated,
  data,
  link,
}: {
  allocated: number
  unallocated: number
  data: ITooltipByUnoccupiedInDepartments[] | undefined
  link: () => void
}) {
  return (
    <Flex style={{ padding: '15px 15px' }} direction="column">
      <span style={{ fontWeight: 600, fontSize: '16px' }}>Available seats breakdown</span>
      <table style={{ width: 'fit-content', minWidth: 150 }}>
        <tbody>
          <tr>
            <td className={css.td}>Unallocated</td>
            <td className={css.td} style={{ textAlign: 'end' }}>
              {unallocated}
            </td>
          </tr>

          <tr>
            <td className={css.td}>Allocated</td>
            <td className={css.td} style={{ textAlign: 'end' }}>
              {allocated}
            </td>
          </tr>
          <tr>
            <td style={{ paddingBottom: 20 }}>
              <ShowDetails data={data} link={link} />
            </td>
          </tr>
          {data?.slice(0, 3).map((d, i) => (
            <tr key={d.name}>
              <td>
                <DepartmentItem id={d.name} name={d.name} color={`#${d.color}`} />
              </td>
              <td style={{ textAlign: 'end' }}>{d.unoccupied}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data && (
        <Flex direction="column">
          <span style={{ margin: 5 }} className={Classes.SKELETON}>
            placeholder
          </span>
          <span style={{ margin: 5 }} className={Classes.SKELETON}>
            placeholder
          </span>
          <span style={{ margin: 5 }} className={Classes.SKELETON}>
            placeholder
          </span>
        </Flex>
      )}
    </Flex>
  )
}

export function TotalSeats({
  data,
  units,
  link,
}: {
  data: ITooltipByUsageType[] | undefined
  units: string | undefined
  link: () => void
}) {
  const unitsForShow = units === 'sqm' ? 'Sqrm' : 'Sqft'
  const scale = useMemo(() => Charts.colorScale(), [])

  return (
    <Flex style={{ padding: '15px 15px' }} direction="column">
      <span style={{ fontWeight: 600, fontSize: '16px', textAlign: 'left' }}>
        Space By Usage Type
      </span>
      <ShowDetails data={data} link={link} />
      {data && (
        <Flex style={{ height: 230 }}>
          <Flex direction="column" justify="flex-end" style={{ width: '40%' }}>
            {data.slice(0, 3).map((s, index) => (
              <DepartmentItem
                key={s.usageType + index}
                id={s.usageType}
                name={s.usageType}
                color={`#${scale(s.usageType)}`}
              />
            ))}
          </Flex>
          <Flex align="center" direction="column" style={{ width: '30%', position: 'relative' }}>
            <div style={{ position: 'absolute' }}>Spaces Count</div>
            <Charts.Donut
              title="Count"
              data={data.map((s, index) => ({
                label: s.usageType,
                value: s.spacesCount,
                color: `#${scale(s.usageType)}`,
              }))}
              height={150}
              width={150}
            />
            <Flex
              direction="column"
              style={{
                lineHeight: '25px',
                width: '55%',
                textAlign: 'right',
                marginTop: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              {data.slice(0, 3).map((s) => (
                <span key={s.usageType}>{s.spacesCount}</span>
              ))}
            </Flex>
          </Flex>
          <Flex align="center" direction="column" style={{ width: '30%', position: 'relative' }}>
            <div style={{ position: 'absolute' }}>
              Allocated area ({unitsForShow ? unitsForShow : 'Sqft'})
            </div>
            <Charts.Donut
              title="Count"
              data={data.slice(0, 3).map((s, index) => ({
                label: s.usageType,
                value: unitsForShow === 'Sqrm' ? s.areaSqrm : s.areaSqft,
                color: `#${scale(s.usageType)}`,
              }))}
              unit={unitsForShow ? unitsForShow : 'Sqft'}
              height={150}
              width={150}
              customTotalFormatter={totalFormatter}
            />
            <Flex
              direction="column"
              style={{
                lineHeight: '25px',
                width: '55%',
                textAlign: 'right',
                marginTop: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              {data.slice(0, 3).map((s) => (
                <span key={s.usageType}>
                  {prettyInteger(units === 'Sqrm' ? s.areaSqrm : s.areaSqft)}
                </span>
              ))}
            </Flex>
          </Flex>
        </Flex>
      )}
      {!data && (
        <Flex direction="column">
          <Flex style={{ margin: '20px 0 20px 120px' }}>
            <div style={{ width: 100, height: 100 }} className={Classes.SKELETON}>
              Placeholder
            </div>
            <div style={{ width: 100, height: 100, marginLeft: 20 }} className={Classes.SKELETON}>
              Placeholder
            </div>
          </Flex>
          <span style={{ margin: '5px 0' }} className={Classes.SKELETON}>
            placeholder
          </span>
          <span style={{ margin: '5px 0' }} className={Classes.SKELETON}>
            placeholder
          </span>
          <span style={{ margin: '5px 0' }} className={Classes.SKELETON}>
            placeholder
          </span>
        </Flex>
      )}
    </Flex>
  )
}

export function DepartmentSeats({
  data,
  link,
}: {
  data: ITooltipByAllocatedInDepartments[] | undefined
  link: () => void
}) {
  const max = data
    ? data.slice(0, 3).reduce((max, d) => Math.max(max, d.occupied + d.unoccupied), 0)
    : 0

  return (
    <Flex style={{ padding: '15px 15px' }} direction="column">
      <span style={{ fontWeight: 600, fontSize: '16px', textAlign: 'left' }}>
        Seats By Department
      </span>
      <ShowDetails data={data} link={link} />
      {data && (
        <Flex style={{ margin: 20, lineHeight: '30px' }} direction="row">
          <Flex justify="flex-end" direction="column">
            {data.slice(0, 3).map((d) => (
              <span className={css.row} title={d.name} style={{ maxWidth: 400 }} key={d.name}>
                {d.name}
              </span>
            ))}
          </Flex>
          <Flex direction="column" style={{ width: '70px', textAlign: 'center' }}>
            <span className={css.row} style={{ fontSize: '12px', color: 'var(--grey1)' }}>
              Allocated
            </span>
            {data.slice(0, 3).map((d) => (
              <span className={css.row} key={d.name}>
                {d.allocated}
              </span>
            ))}
          </Flex>
          <Flex direction="column" style={{ width: '70px', textAlign: 'center' }}>
            <span
              className={css.row}
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                color: 'var(--grey1)',
              }}
            >
              <ColorMarker color={`#${COLORS[2]}`} />
              Occupied
            </span>
            {data.slice(0, 3).map((d) => (
              <span className={css.row} key={d.name}>
                {d.occupied}
              </span>
            ))}
          </Flex>
          <Flex direction="column" style={{ width: '80px', textAlign: 'center' }}>
            <span
              className={css.row}
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                color: 'var(--grey1)',
              }}
            >
              <ColorMarker color={`#${COLORS[0]}`} />
              Unoccupied
            </span>
            {data.slice(0, 3).map((d) => (
              <span className={css.row} key={d.name}>
                {d.unoccupied}
              </span>
            ))}
          </Flex>
          <Flex direction="column" justify="flex-end" style={{ marginLeft: 20 }}>
            {data.slice(0, 3).map((d, i, arr) => (
              <span className={css.row} key={d.name}>
                <Tooltip2
                  content={
                    <DepartmentTooltipContent
                      department={d}
                      occupiedColor={`#${COLORS[2]}`}
                      unoccupiedColor={`#${COLORS[0]}`}
                    />
                  }
                >
                  <SimpleBar
                    val={d.occupied}
                    val2={d.occupied + d.unoccupied}
                    max={max}
                    barColor={`#${COLORS[2]}`}
                    bar2Color={`#${COLORS[0]}`}
                  />
                </Tooltip2>
              </span>
            ))}
          </Flex>
        </Flex>
      )}
      {!data && <TableSkeleton />}
    </Flex>
  )
}
